import { z } from 'zod';
import { dinheiro, paginacao, taxa } from './primitives.js';

/**
 * Parcela = {days, pct}. days 0 = entrada. Lista vazia = a vista.
 * A validacao de soma/Q22 pertence ao motor, nao ao CRUD.
 */
export const parcelaSchema = z.object({
  days: dinheiro,
  pct: taxa,
});

export const CONDICAO_TIPOS = ['A_VISTA', 'PARCELAS_IGUAIS', 'MANUAL', 'ENTRADA_MANUAL_IGUAIS'] as const;
export type CondicaoTipo = (typeof CONDICAO_TIPOS)[number];

/**
 * Entrada da GERACAO da grade (a API regenera SEMPRE; a tela nunca remenda a lista). `quantidade` conta a entrada como parcela
 * ("Entrada + 9" = 10). Sem entrada: 30, 60 ... ; com entrada: 0, 30, 60 ... Entrada percentual so em ENTRADA_MANUAL_IGUAIS (fracao).
 */
export const gerarParcelasSchema = z.object({
  tipo: z.enum(['A_VISTA', 'PARCELAS_IGUAIS', 'ENTRADA_MANUAL_IGUAIS']),
  quantidade: z.number().int('Use um número inteiro').min(1, 'Mínimo 1 parcela').max(120, 'Máximo 120 parcelas').default(1),
  intervaloDias: z.number().int('Use dias inteiros').min(0).max(3650).default(30),
  comEntrada: z.boolean().default(false),
  entradaPercentual: taxa.optional(),
});
export type GerarParcelasDto = z.infer<typeof gerarParcelasSchema>;
export interface GerarParcelasResponse {
  parcelas: { days: string; pct: string }[];
  /** Media ponderada dos prazos pelos percentuais, em precisao cheia. */
  prazoMedio: string;
}

export const criarCondicaoPagamentoSchema = z.object({
  codigo: z.string().trim().min(1).max(40),
  nome: z.string().trim().min(1).max(200),
  ativa: z.boolean().default(true),
  /** MANUAL (ou sem `tipo`, legado): as parcelas daqui valem (soma 100%). Nos demais tipos a API GERA e ignora esta lista. */
  parcelas: z.array(parcelaSchema).max(120).default([]),
  tipo: z.enum(CONDICAO_TIPOS).optional(),
  quantidade: z.number().int().min(1).max(120).optional(),
  intervaloDias: z.number().int().min(0).max(3650).optional(),
  comEntrada: z.boolean().optional(),
  entradaPercentual: taxa.optional(),
});
export type CriarCondicaoPagamentoDto = z.infer<typeof criarCondicaoPagamentoSchema>;

/** Parcelas sao imutaveis apos uso: so nome/ativa editam. Mudar parcelas = nova condicao. */
export const atualizarCondicaoPagamentoSchema = z
  .object({
    nome: z.string().trim().min(1).max(200),
    ativa: z.boolean(),
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, 'Informe ao menos um campo');
export type AtualizarCondicaoPagamentoDto = z.infer<typeof atualizarCondicaoPagamentoSchema>;

export const listarCondicoesPagamentoSchema = paginacao.extend({
  ativa: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
});
export type ListarCondicoesPagamentoQuery = z.infer<typeof listarCondicoesPagamentoSchema>;

export interface ParcelaResponse {
  ordem: number;
  days: string;
  pct: string;
}

export interface CondicaoPagamentoResponse {
  /** Vem do Protheus (rotina em IMPORTAR): os campos de `CAMPOS_DO_PROTHEUS` sao somente leitura; a API recusa a edicao (409 CAMPO_DO_PROTHEUS). */
  doProtheus?: boolean;
  id: string;
  codigo: string;
  nome: string;
  ativa: boolean;
  /** Metadados da geracao (nulos em condicao antiga/importada: prazos gravados, so leitura). */
  tipo: CondicaoTipo | null;
  quantidade: number | null;
  intervaloDias: number | null;
  comEntrada: boolean;
  entradaPercentual: string | null;
  /** Os prazos gravados diferem da regeneracao pelo padrao (chip "prazos divergentes do padrao"). */
  prazosDivergentes: boolean;
  versaoAnteriorId: string | null;
  /** So no detalhe (GET :id). */
  prazoMedio?: string;
  emUso?: boolean;
  parcelas?: ParcelaResponse[];
  createdAt: string;
  updatedAt: string;
}

/** Regenerar: refaz a grade pelos metadados (opcionalmente novos). Condicao NAO usada: troca no lugar; usada: cria nova versao. */
export const regenerarCondicaoSchema = gerarParcelasSchema.partial().extend({
  parcelas: z.array(parcelaSchema).max(120).optional(),
});
export type RegenerarCondicaoDto = z.infer<typeof regenerarCondicaoSchema>;
